/**
 * Words for a check-in result. Every number the page shows is computed here,
 * from the runner's summary, so the page holds no arithmetic of its own.
 */
import { LEARNING_STAGE_DAYS, comparableEarlier, independentDays, type Attempt } from './attempts'
import { PROTOCOL } from './protocol'
import type { InterruptReason, Summary, TrialRecord } from './runner'

export interface Measure {
  label: string
  value: string
  unit: string
  sub: string
}

const f0 = (v: number | null) => (v === null ? '—' : String(Math.round(v)))
const f1 = (v: number | null) => (v === null ? '—' : v.toFixed(1))

/** The four measures, worked out after the run, not during it. */
export function measures(s: Summary): Measure[] {
  return [
    { label: 'Response time', value: f0(s.medianRtMs), unit: 'ms', sub: 'median over valid responses' },
    { label: 'Response speed', value: f1(s.meanSpeed), unit: '/s', sub: 'mean of 1/RT, the PVT’s speed measure' },
    // A COUNT, never "of N" (UI-QA Q1). `lapses` counts timeouts and `validTrials`
    // does not, so "2 of 0" was two numbers about two different populations. The
    // definition and every calculation are the runner's, unchanged.
    { label: 'Lapses', value: String(s.lapses), unit: '', sub: `at or over ${PROTOCOL.lapseMs} ms, or no response` },
    { label: 'False starts', value: String(s.falseStarts), unit: '', sub: 'before the light, or under 100 ms' },
  ]
}

/** `not_assessed` is a real answer: the attempt holds no evidence either way. */
export type CheckState = 'ok' | 'warn' | 'not_assessed'

export interface Check {
  label: string
  state: CheckState
}

/** How an attempt ended, as the runner reported it. */
export interface Ending {
  interrupted: boolean
  /** The runner's reason; `null` when an interrupted attempt does not say why. */
  reason: InterruptReason | null
  /** The trials as recorded, with each one's visibility and focus at arming. */
  trials: readonly Pick<TrialRecord, 'visible' | 'focused'>[]
}

/**
 * "Window stayed in focus", from the evidence and never from `interrupted` alone
 * (UI-QA Q2). A manual Stop interrupts the attempt but loses no focus.
 *
 *  - completed: the runner checks visibility and focus on every frame and ends
 *    the attempt the first frame either is lost, so a completed run kept both;
 *  - `hidden` / `blur`: that is the loss, so the check fails;
 *  - `stopped` / `stale_event`: the frames before the stop found no loss, and
 *    the trials recorded say whether the window had both at each arming. With
 *    no trial recorded there is nothing to read, so it is not assessed;
 *  - an interrupted attempt with no reason: not assessed, never a pass.
 */
export function focusCheck({ interrupted, reason, trials }: Ending): CheckState {
  if (!interrupted) return 'ok'
  if (reason === 'hidden' || reason === 'blur') return 'warn'
  if (reason === null || trials.length === 0) return 'not_assessed'
  return trials.every((t) => t.visible && t.focused) ? 'ok' : 'warn'
}

const state = (ok: boolean): CheckState => (ok ? 'ok' : 'warn')

/** Validity, as four checks with words. Timing here is the browser's own view; see the budget doc. */
export function checks(s: Summary, ending: Ending): Check[] {
  return [
    { label: 'Timing within one frame of intent', state: state(s.p95SchedulingLagMs !== null && s.p95SchedulingLagMs <= 20) },
    { label: 'Window stayed in focus', state: focusCheck(ending) },
    { label: 'Display kept up', state: state(s.trialsWithStall === 0) },
    { label: 'Enough signals', state: state(s.validTrials >= PROTOCOL.minValidTrials) },
  ]
}

export function headline(s: Summary, interrupted: boolean): string {
  if (interrupted) return `Interrupted · stopped at signal ${s.trials}`
  if (!s.valid) return `Not a valid attempt · ${s.invalidReason ?? ''}`
  return `Valid · ${s.validTrials} of ${s.trials} signals`
}

export function comparison(all: readonly Attempt[], current: Attempt): { earlier: number; days: number; learning: boolean; daysToGo: number } {
  const earlier = comparableEarlier(all, current)
  const days = independentDays([...earlier, current])
  return { earlier: earlier.length, days, learning: days < LEARNING_STAGE_DAYS, daysToGo: Math.max(0, LEARNING_STAGE_DAYS - days) }
}
